import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import AIFormGenerator from '@/components/AIFormGenerator';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  type GeneratedForm,
  generateForm
} from '@/services/aiFormService';
import {
  Loader2,
  RefreshCw,
  Edit,
  Save,
  CheckCircle,
  AlertCircle,
  Type,
  Mail,
  Phone,
  List,
  AlignLeft,
  CircleDot,
  CheckSquare,
  Hash,
  Calendar,
  Upload,
  Sparkles,
  BarChart3
} from 'lucide-react';

const fieldIcons: Record<string, React.ReactNode> = {
  text: <Type className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  phone: <Phone className="h-4 w-4" />,
  select: <List className="h-4 w-4" />,
  radio: <CircleDot className="h-4 w-4" />,
  checkbox: <CheckSquare className="h-4 w-4" />,
  textarea: <AlignLeft className="h-4 w-4" />,
  number: <Hash className="h-4 w-4" />,
  date: <Calendar className="h-4 w-4" />,
  file: <Upload className="h-4 w-4" />,
};

const AIBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();

  const [generatedForm, setGeneratedForm] = useState<GeneratedForm | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [refinePrompt, setRefinePrompt] = useState("");
  const [isRefining, setIsRefining] = useState(false);
  const [refineError, setRefineError] = useState<string | null>(null);

  const handleRefine = async () => {
    if (!refinePrompt.trim() || !generatedForm) return;
    
    setIsRefining(true);
    setRefineError(null);
    try {
      const data = await generateForm(refinePrompt, generatedForm);
      try {
        const cleanedData = (typeof data === 'string' ? data : JSON.stringify(data)).trim().replace(/^```json/, '').replace(/```$/, '');
        const updatedForm = JSON.parse(cleanedData);
        if (updatedForm && typeof updatedForm === 'object' && Array.isArray(updatedForm.fields)) {
          setGeneratedForm(updatedForm);
        } else {
          throw new Error('AI returned invalid form structure');
        }
      } catch (e) {
        console.error('Refinement parse error:', e);
        setRefineError('The AI returned a response that could not be parsed. Please try again.');
        toast({ title: 'Refinement failed', description: 'AI response was malformed.', variant: 'destructive' });
      }
    } catch (err: any) {
      if (err.message?.includes('AI_QUOTA_EXCEEDED')) {
        setRefineError("QUOTA_REACHED");
      } else {
        setRefineError(err.message || 'Failed to refine form. Try again.');
      }
    } finally {
      setIsRefining(false);
    }
  };

  const handleRefineKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isRefining && refinePrompt.trim()) {
        handleRefine();
      }
    }
  };

  const handleSave = async (publish: boolean) => {
    if (!generatedForm || !user) return;
    setIsSaving(true);
    try {
      const { data: newForm, error } = await supabase
        .from('forms')
        .insert({
          user_id: user.id,
          title: (generatedForm.title || 'Untitled AI Form').trim() || 'Untitled AI Form',
          description: (generatedForm.description || '').trim(),
          fields: (Array.isArray(generatedForm.fields) ? generatedForm.fields : []).map((f, i) => ({
            id: String(f.id || `field_${Date.now()}_${i}`),
            type: (f.type || 'text'),
            label: String(f.label || 'New Field'),
            placeholder: String(f.placeholder || ''),
            required: !!f.required,
            options: Array.isArray(f.options) ? f.options.map(String) : []
          })),
          is_published: publish,
          whatsapp_number: generatedForm.whatsapp_number || '',
          settings: { whatsappMessage: 'Hi! I would like to submit this form: {form_data}' },
        })
        .select('id')
        .single();

      if (error) throw error;
      toast({ title: publish ? 'Form published!' : 'Form saved!' });
      navigate(`/form-builder/${newForm.id}`);
    } catch (err: any) {
      toast({ title: 'Failed to save', description: err.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setGeneratedForm(null);
  };

  const intent = searchParams.get('intent') || '';

  return (
    <DashboardLayout>
      <Helmet>
        <title>Create with AI | Bravax Forms</title>
      </Helmet>
      {!generatedForm ? (
        <div className="flex-1 flex flex-col items-center justify-center py-12 px-4 min-h-[calc(100vh-3.5rem)]">
          <AIFormGenerator onFormGenerated={setGeneratedForm} initialPrompt={intent} />
        </div>
      ) : (
        /* Side-by-side: Chat + Preview */
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[380px_1fr] h-[calc(100vh-3.5rem)] overflow-hidden animate-in fade-in duration-500">
          {/* Left: Controls (Sidebar) */}
          <div className="flex flex-col border-r border-border/5 bg-surface-low/40 backdrop-blur-3xl overflow-y-auto">
            <div className="flex-1 flex flex-col px-6 py-10">
              <div className="w-full space-y-10">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-sm border border-green-500/20">
                  <CheckCircle className="h-4 w-4" />
                  Form generated · {generatedForm.fields.length} questions
                </div>

                <div className="space-y-3">
                  <h2 className="text-2xl font-display font-bold text-foreground leading-tight">{generatedForm.title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed font-medium">{generatedForm.description}</p>
                </div>

                <div className="glass-card rounded-[2.5rem] p-6 space-y-4 border-primary/10 shadow-xl shadow-primary/5">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-bold text-primary uppercase tracking-widest">Architect Interaction</span>
                    <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                  </div>
                  <Textarea
                    placeholder="e.g., Add a field for 'Company Name'..."
                    className="min-h-[100px] resize-none text-base bg-surface-low/50 border-none rounded-3xl focus-visible:ring-1 focus-visible:ring-primary/20 placeholder:text-muted-foreground/20 shadow-inner p-5"
                    value={refinePrompt}
                    onChange={(e) => setRefinePrompt(e.target.value)}
                    onKeyDown={handleRefineKeyDown}
                    disabled={isRefining}
                  />
                  {refineError && (
                    <div className={`p-4 rounded-xl flex flex-col gap-3 ${
                      refineError === "QUOTA_REACHED" 
                        ? "bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-500" 
                        : "bg-destructive/10 border border-destructive/20 text-destructive"
                    }`}>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        {refineError === "QUOTA_REACHED" ? "Daily AI Generation Limit Reached" : refineError}
                      </div>
                      {refineError === "QUOTA_REACHED" && (
                        <div className="space-y-3">
                          <p className="text-xs opacity-90 leading-relaxed">
                            You've reached your free limit. Upgrade to <strong>Pro</strong> to keep refining this form!
                          </p>
                          <Button asChild size="sm" variant="default" className="w-full bg-amber-600 hover:bg-amber-700 text-white border-none h-8 rounded-lg">
                            <Link to="/billing">Upgrade Now</Link>
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  <Button 
                    onClick={handleRefine} 
                    disabled={isRefining || !refinePrompt.trim()} 
                    className="w-full h-14 rounded-2xl btn-gradient text-[10px] uppercase tracking-widest gap-2"
                  >
                    {isRefining ? <><Loader2 className="h-4 w-4 animate-spin"/> Adapting Structure...</> : <><RefreshCw className="h-4 w-4" /> Apply Refinements</>}
                  </Button>
                </div>

                <div className="flex flex-col gap-4 pt-6 mt-auto">
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      onClick={() => handleSave(false)} 
                      disabled={isSaving} 
                      variant="ghost" 
                      className="w-full gap-2 h-12 rounded-2xl border border-border/10 bg-surface-low/30 hover:bg-surface-high hover:text-primary transition-all duration-300 text-[9px] font-bold uppercase tracking-widest"
                    >
                      <Edit className="h-3.5 w-3.5" /> Builder
                    </Button>
                    <Button 
                      onClick={handleReset} 
                      variant="ghost" 
                      className="w-full gap-2 h-12 rounded-2xl border border-border/10 bg-surface-low/30 hover:bg-surface-high text-muted-foreground hover:text-foreground transition-all duration-300 text-[9px] font-bold uppercase tracking-widest"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Reset
                    </Button>
                  </div>
                  
                  <Button 
                    onClick={() => handleSave(true)} 
                    disabled={isSaving} 
                    className="w-full h-16 rounded-[2rem] btn-gradient gap-3 text-sm font-bold shadow-2xl shadow-primary/20"
                  >
                    {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                    Save & Publish Final Form
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form Preview */}
          <div className="bg-surface-lowest overflow-y-auto relative border-l border-border/5 scrollbar-none">
            <div className="max-w-4xl mx-auto py-16 px-12 relative">
              <div className="flex items-center gap-3 mb-12">
                 <div className="h-px w-8 bg-primary/30" />
                 <span className="text-[10px] font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full">Live Architecture Preview</span>
              </div>

              <Card className="border-none bg-surface-low/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl shadow-primary/5">
                <div className="p-10 md:p-16 border-b border-border/5 bg-surface-low/20">
                  <h3 className="text-3xl md:text-4xl font-display font-bold tracking-tightest leading-tight text-foreground">{generatedForm.title}</h3>
                  {generatedForm.description && (
                    <p className="text-lg text-muted-foreground mt-4 font-medium leading-relaxed">{generatedForm.description}</p>
                  )}
                </div>

                <div className="p-10 md:p-16 space-y-12">
                  {generatedForm.fields.map((field, i) => (
                    <div
                      key={field.id}
                      className="group space-y-4 animate-in fade-in slide-in-from-bottom-4"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                           <span className="text-primary/70">{fieldIcons[field.type] || <Type className="h-4 w-4" />}</span>
                           {field.label}
                           {field.required && <span className="text-primary">*</span>}
                        </label>
                        <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-widest opacity-30 group-hover:opacity-100 transition-opacity">
                           {field.type}
                        </Badge>
                      </div>

                      <div className="relative">
                        {field.type === 'textarea' ? (
                          <div className="min-h-[140px] rounded-2xl border-none bg-surface-low/50 px-6 py-4 text-base text-muted-foreground/60 italic shadow-inner">
                            {field.placeholder || 'Write your response here...'}
                          </div>
                        ) : field.type === 'select' || field.type === 'radio' || field.type === 'checkbox' ? (
                          <div className="space-y-3">
                            {field.options?.map((opt, oi) => (
                              <div key={oi} className="flex items-center gap-4 text-base text-muted-foreground bg-surface-low/30 p-4 rounded-xl border border-border/5 shadow-sm group/opt hover:bg-surface-low/60 transition-all cursor-default">
                                <div className={cn(
                                  "w-5 h-5 border-2 border-primary/20 flex-shrink-0 transition-all group-hover/opt:border-primary/50",
                                  field.type === 'radio' ? "rounded-full" : "rounded-lg",
                                  field.type === 'checkbox' && "bg-primary/5"
                                )} />
                                {opt}
                              </div>
                            ))}
                            {(!field.options || field.options.length === 0) && (
                              <div className="p-4 rounded-xl border border-dashed border-border/20 text-muted-foreground/40 text-xs text-center italic">
                                No options defined
                              </div>
                            )}
                          </div>
                        ) : field.type === 'file' ? (
                          <div className="h-24 rounded-2xl border-2 border-dashed border-border/20 bg-surface-low/30 flex flex-col items-center justify-center gap-2 text-muted-foreground/40 group-hover:border-primary/20 transition-colors">
                             <Upload className="h-5 w-5" />
                             <span className="text-[10px] font-bold uppercase tracking-widest">Click to upload or drag & drop</span>
                          </div>
                        ) : (
                          <div className="h-16 rounded-2xl border-none bg-surface-low/50 px-6 flex items-center text-base text-muted-foreground/60 italic shadow-inner relative">
                            {field.placeholder || `Enter ${field.type}...`}
                            {field.type === 'date' && <Calendar className="absolute right-6 h-4 w-4 text-muted-foreground/30" />}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <div className="pt-8">
                     <Button 
                       disabled 
                       className="w-full h-16 rounded-2xl bg-surface-high text-muted-foreground/40 cursor-not-allowed border border-border/10 font-bold uppercase tracking-widest text-[10px] shadow-inner"
                     >
                        Finalize Structure to Enable Live Interactions
                     </Button>
                  </div>
                </div>
              </Card>

              <div className="mt-12 flex flex-col items-center gap-4 opacity-50">
                 <div className="flex items-center gap-2 px-4 py-2 bg-surface-low/50 rounded-full border border-border/5">
                    <Sparkles className="h-3 w-3 text-primary" />
                    <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Orchestrated by Digital Architect</span>
                 </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AIBuilder;

